import { GanttDeepLink, GanttTask } from '../types/gantt.types'
import { daysBetween } from '../constants/gantt.logic'
import { SiteInfo } from '../views/CalendarMultiView'
import { useEffect } from 'react'
import { toast } from 'sonner'
// 딥링크 소비 — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttDeepLinkArgs = {
  deepLink: GanttDeepLink | null | undefined
  selectedSite: SiteInfo | undefined
  loadedScheduleId: number | null
  tasks: GanttTask[]
  onDeepLinkConsumed: (() => void) | undefined
  setShowSigongPanel: React.Dispatch<React.SetStateAction<boolean>>
  openMaterialPopup: (taskId: number | null) => void
  setDeepLinkTaskId: React.Dispatch<React.SetStateAction<number | null>>
  setShowShipPanel: React.Dispatch<React.SetStateAction<boolean>>
  selectPanelTaskGuarded: (taskId: number) => void
  setCardSup: React.Dispatch<React.SetStateAction<{ taskId: number; nonce: number; } | null>>
  /** 카드 pt — 시공파트너 카드 진행 요약 펼침 요청(card=sup 과 같은 번호표 방식) */
  setCardPt: React.Dispatch<React.SetStateAction<{ taskId: number; nonce: number; } | null>>
  /** 카드 cr — 고객 요청 카드로 이동(앵커 스크롤) 요청(card=sup 과 같은 번호표 방식) */
  setCardCr: React.Dispatch<React.SetStateAction<{ taskId: number; nonce: number; } | null>>
  /** card=bar — 막대 외곽선 강조 대상(다음 조작까지 유지) */
  setFocusBarId: React.Dispatch<React.SetStateAction<number | null>>
  /** 공종 지정 딥링크 — 해당 행으로 스크롤 요청(horizontal: 막대가 가로로도 보이게 맞춤) */
  setFocusReq: React.Dispatch<React.SetStateAction<{ taskId: number; nonce: number; horizontal: boolean; } | null>>
  hideBars: boolean
  setHideBars: React.Dispatch<React.SetStateAction<boolean>>
  ganttPeriod: '전체' | '1주' | '2주' | '월'
  setGanttPeriod: React.Dispatch<React.SetStateAction<'전체' | '1주' | '2주' | '월'>>
  ganttStart: Date
  visibleDayRangeRef: React.MutableRefObject<[number, number]>
  setExpandedRows: React.Dispatch<React.SetStateAction<Set<string>>>
}

export function useGanttDeepLink({
  deepLink, selectedSite, loadedScheduleId, tasks, onDeepLinkConsumed, setShowSigongPanel, openMaterialPopup,
  setDeepLinkTaskId, setShowShipPanel, selectPanelTaskGuarded, setCardSup, setCardPt, setCardCr, setFocusBarId, setFocusReq,
  hideBars, setHideBars, ganttPeriod, setGanttPeriod, ganttStart, visibleDayRangeRef, setExpandedRows,
}: UseGanttDeepLinkArgs) {
  // ── 딥링크 소비 — 주소의 taskId·card·pop 으로 들어온 요청을 한 번만 처리한다 ──────
  // 그 현장의 공종 목록 조회가 끝난 뒤에만 처리한다. pop 이 있으면 해당 팝업을 열고(taskId 는 강조용),
  // 없으면 taskId 공종의 상세창을 연다(card=bar 는 상세창 없이 막대 강조만). taskId 가 있으면 어느 경우든 그 공종 행으로 스크롤한다(F-23).
  useEffect(() => {
    if (!deepLink || !selectedSite || selectedSite.siteId !== deepLink.siteId) return
    if (loadedScheduleId !== selectedSite.scheduleId) return
    const { taskId, pop, card } = deepLink
    if (taskId !== null && !tasks.some(t => t.taskId === taskId)) {
      toast.info('이 공종을 찾을 수 없어요. 간트 기본 화면으로 열었어요.')
      onDeepLinkConsumed?.()
      return
    }
    // 이전 딥링크의 강조는 새 딥링크가 오면 거둔다
    setFocusBarId(null)
    if (taskId !== null) setFocusReq(prev => ({ taskId, nonce: (prev?.nonce ?? 0) + 1, horizontal: pop === null && card === 'bar' }))
    if (pop === 'pt-assign') setShowSigongPanel(true)
    else if (pop === 'sup-order') openMaterialPopup(taskId)
    else if (pop === 'ship') { setDeepLinkTaskId(taskId); setShowShipPanel(true) }
    else if (taskId !== null && card === 'bar') {
      // 막대가 보이게 만든 뒤 강조한다 — 바 숨기기 해제, 표시 범위 밖이면 '전체'로 복귀, 접힌 구간 행이면 펼침
      const t = tasks.find(x => x.taskId === taskId)
      if (t) {
        if (hideBars) setHideBars(false)
        if (ganttPeriod !== '전체') {
          const [from, to] = visibleDayRangeRef.current
          if (daysBetween(ganttStart, t.endDate) < from || daysBetween(ganttStart, t.startDate) > to) setGanttPeriod('전체')
        }
        if (tasks.filter(x => x.workTypeCode === t.workTypeCode).length > 1) setExpandedRows(prev => new Set(prev).add(t.workTypeCode))
      }
      setFocusBarId(taskId)
    } else if (taskId !== null) {
      selectPanelTaskGuarded(taskId)
      // 카드 sup — 상세창을 열고 자재파트너 카드의 진행 요약을 펼친다
      if (card === 'sup') setCardSup(prev => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }))
      // 카드 pt — 상세창을 열고 시공파트너 카드의 진행 요약을 펼친다
      if (card === 'pt') setCardPt(prev => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }))
      // 카드 cr — 상세창을 열고 고객 요청 카드로 이동한다
      if (card === 'cr') setCardCr(prev => ({ taskId, nonce: (prev?.nonce ?? 0) + 1 }))
    }
    onDeepLinkConsumed?.()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, selectedSite, loadedScheduleId, tasks])
}
